import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, Minus, Plus, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { WHATSAPP_NUMBER } from "@/constants/brand";

const FALLBACK_CONTENTS = ["Dal fry", "4 ghee rotis", "Steamed rice", "2 seasonal Jain sabzis", "Papad", "Achar", "Fresh salad", "Sweet of the day"];

function Stepper({ value, onDec, onInc, testid, max }) {
  return (
    <div className="mt-2 flex items-center gap-5" data-testid={`${testid}-stepper`}>
      <button
        onClick={onDec}
        data-testid={`${testid}-decrease-button`}
        aria-label="Decrease"
        className="grid h-12 w-12 place-items-center rounded-full border-2 border-emerald-900/20 text-emerald-950 transition-colors hover:bg-emerald-900 hover:text-amber-50"
      >
        <Minus className="h-5 w-5" />
      </button>
      <span className="w-10 text-center font-display text-4xl font-bold text-emerald-950" data-testid={`${testid}-value`}>{value}</span>
      <button
        onClick={onInc}
        data-testid={`${testid}-increase-button`}
        aria-label="Increase"
        className="grid h-12 w-12 place-items-center rounded-full border-2 border-emerald-900/20 text-emerald-950 transition-colors hover:bg-emerald-900 hover:text-amber-50"
      >
        <Plus className="h-5 w-5" />
      </button>
      {max && <span className="text-xs font-semibold text-emerald-900/50">max {max}</span>}
    </div>
  );
}

export default function ThaliOrder({ stations, menu, selectedStationId, onSelectStation }) {
  const [qty, setQty] = useState(1);
  const [rotiQty, setRotiQty] = useState(0);
  const [open, setOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", pnr: "", coach: "", seat: "" });

  const item = useMemo(
    () => menu.find((m) => m.name === "Jain Special Thali") || menu[0],
    [menu]
  );
  const rotiItem = useMemo(() => menu.find((m) => m.name === "Extra Ghee Roti"), [menu]);

  const price = item?.price ?? 250;
  const rotiPrice = rotiItem?.price ?? 15;
  const contents = item?.contents?.length ? item.contents : FALLBACK_CONTENTS;
  const station = useMemo(
    () => stations.find((s) => s._id === selectedStationId) || null,
    [stations, selectedStationId]
  );
  const thaliTotal = price * qty;
  const rotiTotal = rotiPrice * rotiQty;
  const subtotal = thaliTotal + rotiTotal;
  const delivery = station?.delivery_charge ?? 0;
  const total = subtotal + delivery;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const whatsappUrl = (order) => {
    const lines = [
      "New Order — Jain Food on Train",
      `Ref: ${order.reference}`,
      `Name: ${order.customer_name}`,
      `Phone: ${order.phone}`,
      order.pnr ? `PNR: ${order.pnr}` : null,
      order.coach || order.seat ? `Coach/Seat: ${order.coach} ${order.seat}`.trim() : null,
      `Station: ${order.station_name} (${order.station_code})`,
      `Item: ${order.item_name} x ${order.quantity}`,
      order.extra_roti_qty > 0 ? `Extra Ghee Roti x ${order.extra_roti_qty}` : null,
      `Subtotal: Rs.${order.subtotal}`,
      `Delivery: Rs.${order.delivery_charge}`,
      `Total: Rs.${order.total}`,
    ].filter(Boolean);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!station) {
      toast.error("Please choose your delivery station");
      return;
    }
    setPlacing(true);
    try {
      const { data: order } = await api.post("/orders", {
        customer_name: form.name,
        phone: form.phone,
        pnr: form.pnr,
        coach: form.coach,
        seat: form.seat,
        station_id: station._id,
        quantity: qty,
        extra_roti_qty: rotiQty,
      });
      setPlaced(order);
      window.open(whatsappUrl(order), "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPlacing(false);
    }
  };

  return (
    <section id="order" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32" data-testid="order-section">
      <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-amber-700">The signature</p>
          <h2 className="font-display text-4xl font-bold tracking-tight text-emerald-950 sm:text-5xl" data-testid="thali-heading">
            Jain Special <span className="italic text-amber-600">Thali</span>
          </h2>
          <p className="mt-4 max-w-lg text-base text-emerald-950/70 sm:text-lg" data-testid="thali-description">
            {item?.description ||
              "Pure satvik Jain thali — no onion, no garlic, no root vegetables. Cooked fresh and delivered hot to your berth."}
          </p>
          <p className="mt-6 font-display text-5xl font-bold text-emerald-950" data-testid="thali-price">
            ₹{price}
            <span className="ml-3 align-middle text-base font-semibold text-emerald-900/60">+ station delivery</span>
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="thali-contents">
            {contents.map((c) => (
              <li key={c} className="flex items-center gap-3 rounded-2xl border border-amber-900/10 bg-white/70 px-4 py-3 text-sm font-semibold text-emerald-950">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-900">
                  <Check className="h-3.5 w-3.5 text-amber-300" />
                </span>
                {c}
              </li>
            ))}
          </ul>
          <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-50 px-5 py-3.5" data-testid="extra-roti-note">
            <Plus className="h-4 w-4 text-amber-700" />
            <p className="text-sm font-semibold text-emerald-950">
              Need more? Add extra ghee rotis at <span className="font-bold text-amber-700">₹{rotiPrice} each</span> in your order.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="rounded-3xl border border-amber-900/10 bg-white/80 p-7 shadow-xl shadow-amber-900/10 backdrop-blur sm:p-9"
          data-testid="order-card"
        >
          <h3 className="font-display text-2xl font-bold text-emerald-950">Build your order</h3>

          <label className="mt-6 block text-xs font-bold uppercase tracking-[0.18em] text-emerald-900/60">
            Delivery station
          </label>
          <Select value={selectedStationId} onValueChange={onSelectStation}>
            <SelectTrigger className="mt-2 rounded-2xl border-emerald-900/15 py-6 text-base font-semibold" data-testid="order-station-select">
              <SelectValue placeholder="Choose your station" />
            </SelectTrigger>
            <SelectContent>
              {stations.map((s) => (
                <SelectItem key={s._id} value={s._id} disabled={!s.delivery_available} data-testid={`order-station-option-${s.code}`}>
                  {s.name} ({s.code}) — ₹{s.delivery_charge} delivery
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.18em] text-emerald-900/60">Thalis</label>
              <Stepper testid="qty" value={qty} max={25} onDec={() => setQty((q) => Math.max(1, q - 1))} onInc={() => setQty((q) => Math.min(25, q + 1))} />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.18em] text-emerald-900/60">
                Extra rotis <span className="normal-case text-amber-700">₹{rotiPrice} each</span>
              </label>
              <Stepper testid="roti-qty" value={rotiQty} max={20} onDec={() => setRotiQty((q) => Math.max(0, q - 1))} onInc={() => setRotiQty((q) => Math.min(20, q + 1))} />
            </div>
          </div>

          <div className="mt-8 space-y-3 rounded-2xl bg-amber-50 p-5" data-testid="order-summary">
            <div className="flex justify-between text-sm font-semibold text-emerald-950/80">
              <span>Thali × {qty}</span>
              <span data-testid="summary-thali">₹{thaliTotal}</span>
            </div>
            {rotiQty > 0 && (
              <div className="flex justify-between text-sm font-semibold text-emerald-950/80">
                <span>Extra roti × {rotiQty}</span>
                <span data-testid="summary-roti">₹{rotiTotal}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-semibold text-emerald-950/80">
              <span>Delivery {station ? `(${station.code})` : ""}</span>
              <span data-testid="summary-delivery">{station ? `₹${delivery}` : "—"}</span>
            </div>
            <div className="flex justify-between border-t border-amber-900/15 pt-3 text-lg font-bold text-emerald-950">
              <span>Total</span>
              <span data-testid="summary-total">₹{total}</span>
            </div>
          </div>

          <button
            onClick={() => {
              if (!station) {
                toast.error("Please choose your delivery station first");
                return;
              }
              setPlaced(null);
              setOpen(true);
            }}
            data-testid="place-order-button"
            className="mt-7 w-full rounded-full bg-emerald-900 py-4 text-base font-bold text-amber-50 shadow-lg shadow-emerald-900/25 transition-all hover:-translate-y-0.5 hover:bg-emerald-800"
          >
            Place Order
          </button>
          <p className="mt-3 text-center text-xs text-emerald-900/60">
            Confirmed instantly on WhatsApp — pay on delivery.
          </p>
        </motion.div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="order-dialog">
          {placed ? (
            <div className="py-4 text-center" data-testid="order-success">
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-emerald-100">
                <Check className="h-7 w-7 text-emerald-700" />
              </div>
              <DialogHeader>
                <DialogTitle className="text-center font-display text-2xl">Order placed!</DialogTitle>
              </DialogHeader>
              <p className="mt-2 text-sm text-emerald-950/70">
                Reference <span className="font-bold text-emerald-950" data-testid="order-reference">{placed.reference}</span>
              </p>
              <p className="mt-1 text-sm text-emerald-950/70">
                Total ₹{placed.total} · pay on delivery at {placed.station_name}
              </p>
              <a
                href={whatsappUrl(placed)}
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-confirm-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#1eb856]"
              >
                <MessageCircle className="h-4 w-4" /> Confirm on WhatsApp
              </a>
            </div>
          ) : (
            <form onSubmit={placeOrder} data-testid="order-form">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Your details</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="order-name-input"
                  className="h-12 w-full rounded-2xl border border-emerald-900/15 bg-transparent px-4 font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
                <input required value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                  inputMode="numeric" placeholder="Mobile number (10-digit)" data-testid="order-phone-input"
                  className="h-12 w-full rounded-2xl border border-emerald-900/15 bg-transparent px-4 font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
                <input value={form.pnr} onChange={(e) => setForm((f) => ({ ...f, pnr: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                  inputMode="numeric" placeholder="PNR (optional)" data-testid="order-pnr-input"
                  className="h-12 w-full rounded-2xl border border-emerald-900/15 bg-transparent px-4 font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
                <div className="grid grid-cols-2 gap-3">
                  <input value={form.coach} onChange={set("coach")} placeholder="Coach (e.g. B4)" data-testid="order-coach-input"
                    className="h-12 w-full rounded-2xl border border-emerald-900/15 bg-transparent px-4 font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
                  <input value={form.seat} onChange={set("seat")} placeholder="Seat (e.g. 32)" data-testid="order-seat-input"
                    className="h-12 w-full rounded-2xl border border-emerald-900/15 bg-transparent px-4 font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500" />
                </div>
                <button type="submit" disabled={placing} data-testid="order-submit-button"
                  className="w-full rounded-full bg-amber-600 py-3.5 text-base font-bold text-amber-50 transition-colors hover:bg-amber-700 disabled:opacity-50">
                  {placing ? "Placing…" : `Confirm Order — ₹${total}`}
                </button>
                <p className="text-center text-xs text-emerald-900/60">
                  WhatsApp opens with your order pre-filled — just press send.
                </p>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
